// Pass-through costs for the Billing tab — delivery and freight charges, and
// items the client buys through us outright (4 gas monitors), that the job
// re-bills at cost (or at the job's own pass-through %) instead of the
// cost-plus markup for their cost class.
//
// 4 gas monitors on 54026070 are Gosafe PO 00231491 line 1 "GX3R W/ 4 GAS"
// (56500 Safety and PPE, 100 @ $500 over two receipts plus tax), shipped on
// a "Drop Ship Estimate" line on the same PO.
//
// Freight never gets a cost type of its own in JDE: it rides the PO it was
// bought on, as its own PO line ("Freight In", "Estimated Freight In",
// "Freight Out", "Freight Install", "Expedited Delivery Allowance" …) under
// whatever cost type the PO carries — 52200 rentals, 53200 mats, 56100 lease,
// 56500 PPE all had some on 54026070/71 — and its sales tax posts as a second
// row with the same description. So the rule reads the line's description
// (Remark_Description), with whole cost types (54300 Hauling and
// Transportation, say) as a second, opt-in way in.
//
// Job settings blob (settings.pt, saved on the billing settings row):
//   on — auto-tag lines by the rules below (per-line overrides still apply
//        when off)
//   mk — markup % pass-through lines bill at; 0 = straight at cost
//   kw — description keywords, matched case-insensitively as substrings
//        ('deliver' catches delivery / delivered)
//   ct — cost types whose every non-labor line passes through
//
// Per line, meta.pt 'Y' / 'N' forces a line in or out regardless of the
// rules. A line on a summary freezes pt in its snapshot like markup, so a
// rule edit never moves issued paper.
//
// Labor never passes through: ST/OT bill off the rate sheet and per diem is
// already at cost.

export const PT_GROUP = 'Pass-Through';

export const PT_SETTINGS_DEFAULT = { on: true, mk: 0, kw: ['freight', 'deliver', 'drop ship', '4 gas'], ct: [] };

// Descriptions and keywords compare with hyphens/underscores as spaces and
// runs of whitespace collapsed, so '4 gas' also catches "4-GAS" and
// "Drop-Ship".
const normText = (s) => String(s ?? '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();

const pNum = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[$,%\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};

// Keywords arrive as an array (stored) or the comma-separated string the
// Rates & Rules input holds while it's being typed.
export const parseKeywords = (kw) => [...new Set(
    (Array.isArray(kw) ? kw : String(kw ?? '').split(','))
        .map((k) => String(k || '').trim().toLowerCase())
        .filter(Boolean),
)];

export const normPt = (pt) => {
    const p = { ...PT_SETTINGS_DEFAULT, ...(pt || {}) };
    return {
        on: p.on !== false,
        mk: pNum(p.mk),
        kw: parseKeywords(p.kw),
        ct: [...new Set((Array.isArray(p.ct) ? p.ct : []).map((c) => String(c || '').trim()).filter(Boolean))],
    };
};

// Why the rules tag a line pass-through — '' when they don't. `line` is
// { cls, ct, ds }: billing.js's cost class, the cost type code and the JDE
// line description.
export const ptRuleMatch = (line, ptIn) => {
    const pt = normPt(ptIn);
    if (!pt.on || !line || line.cls === 'Labor') return '';
    const ct = String(line.ct || '');
    // Fleet usage (52100) is an equipment timecard, not a purchase — it bills
    // hours × the rate sheet. A 4 gas monitor logged in HeavyJob carries
    // "4 GAS" in its description and would otherwise land here and bill at
    // cost instead of its $8.00 day rate.
    if (ct === '52100') return '';
    if (ct && pt.ct.includes(ct)) return `cost type ${ct}`;
    const ds = normText(line.ds);
    const kw = ds ? pt.kw.find((k) => normText(k) && ds.includes(normText(k))) : '';
    return kw ? `"${kw}" in description` : '';
};

// Final call for an unbilled line: a per-line override ('Y' / 'N') wins over
// the rules.
export const isPassThru = (line, ptIn, ov) => {
    if (!line || line.cls === 'Labor') return false;
    if (ov === 'Y') return true;
    if (ov === 'N') return false;
    return !!ptRuleMatch(line, ptIn);
};
